import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { TrendingUp, Cpu, Mail } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'
import { MotionTokens } from '../styles/motion-tokens'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const sectionReveal = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: {
    duration: MotionTokens.sectionReveal.duration,
    ease: [0.22, 1, 0.32, 1],
  },
}

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

export default function InvestorsPage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="mb-6 flex justify-center">
              <TrendingUp className="w-16 h-16 text-trust-blue" strokeWidth={1} />
            </div>
            <h1
              className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
              style={brandFont}
            >
              {'INV\u039EST\u0398RS'}
            </h1>
            <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              Building the infrastructure for conscious AI
            </p>
          </motion.div>
        </div>
      </section>

      {/* Investment Thesis */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" {...sectionReveal}>
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Investment <span className="text-trust-blue">Thesis</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              Consciousness-first AI represents the next platform shift in enterprise technology
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Market Opportunity',
                desc: 'Enterprise AI spending is projected to exceed $500B by 2028. LUKHAS targets the fastest-growing segment: ethically governed, consciousness-aware systems.',
                items: ['Rapidly expanding TAM in regulated industries', 'First-mover in Guardian-enforced AI ethics', 'Platform model with ecosystem network effects'],
              },
              {
                title: 'Competitive Moat',
                desc: 'Our eight-star Constellation architecture delivers capabilities that single-model competitors cannot replicate without fundamental re-architecture.',
                items: ['Proprietary multi-system consciousness framework', 'Patent-pending CollapseHash identity layer', 'Deep enterprise integration partnerships'],
              },
              {
                title: 'Growth Strategy',
                desc: 'Land-and-expand with enterprise customers through modular Constellation services, driving organic expansion from single use-case to full platform adoption.',
                items: ['SaaS + consumption revenue model', 'Geographic expansion via regional hubs', 'Developer ecosystem flywheel'],
              },
            ].map(({ title, desc, items }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-8">
                    <h3 className="text-xl tracking-wide mb-3 text-trust-blue" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-white/50 mb-4" style={bodyFont}>{desc}</p>
                    <ul className="space-y-2 text-sm text-white/40">
                      {items.map((item) => (
                        <li key={item} style={bodyFont}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Overview */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div className="text-center mb-16" {...sectionReveal}>
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Technology <span className="text-trust-blue">Overview</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto" style={bodyFont}>
              A purpose-built architecture where awareness, memory, and ethics operate as
              coordinated systems rather than isolated models
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                title: 'Constellation Architecture',
                desc: 'Eight specialized subsystems orchestrated through a unified consciousness signal router: Anchor, Trail, Horizon, Watch, Bio, Dream, Ethics, and Quantum.',
                sub: 'Each system can be deployed independently or composed into full-stack consciousness pipelines for enterprise workloads.',
              },
              {
                title: 'Guardian Ethics Engine',
                desc: 'Constitutional AI guardrails enforced at the infrastructure layer, not as an afterthought. Every model output passes through real-time ethical compliance checks.',
                sub: 'Auditable decision logs, drift detection, and automated intervention provide the trust foundation enterprises require.',
              },
            ].map(({ title, desc, sub }) => (
              <motion.div key={title} {...sectionReveal}>
                <GlassCard>
                  <div className="p-8">
                    <div className="mb-4">
                      <Cpu className="w-10 h-10 text-trust-blue" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-xl tracking-wide mb-3 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                      {title}
                    </h3>
                    <p className="text-white/50 mb-4" style={bodyFont}>{desc}</p>
                    <p className="text-sm text-white/40" style={bodyFont}>{sub}</p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact IR */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...sectionReveal}>
            <div className="mb-6 flex justify-center">
              <Mail className="w-12 h-12 text-trust-blue" strokeWidth={1.5} />
            </div>
            <h2
              className="text-4xl md:text-5xl tracking-[0.1em] mb-6 text-white"
              style={brandFont}
            >
              Contact <span className="text-trust-blue">Investor Relations</span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto mb-12" style={bodyFont}>
              For institutional inquiries, due diligence materials, or to schedule a
              management presentation, please reach out to our investor relations team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:ir@lukhas.com"
                className="px-12 py-3 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
                style={bodyFont}
              >
                ir@lukhas.com
              </a>
              <Link
                to="/contact"
                className="px-12 py-3 rounded-full border border-trust-blue/40 text-trust-blue text-sm tracking-[0.06em] hover:border-trust-blue/70 transition-all inline-block"
                style={bodyFont}
              >
                General Inquiries
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter
        columns={[
          {
            title: 'Investors',
            links: [
              { label: 'Investment Thesis', href: '/investors' },
              { label: 'Technology', href: '/investors' },
              { label: 'Contact IR', href: '/investors' },
            ],
          },
          {
            title: 'Company',
            links: [
              { label: 'Home', href: '/' },
              { label: 'Governance', href: '/governance' },
              { label: 'Contact', href: '/contact' },
            ],
          },
          {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Cookie Policy', href: '/cookies' },
            ],
          },
        ]}
      />
    </div>
  )
}
